import { db } from "../../config/firebaseConfig";
import {
  increment,
  doc,
  getDoc,
  setDoc,
  runTransaction,
  query,
  collection,
  where,
  orderBy,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-hot-toast";

/**
 * Check if a player is eligible to stake (balance > 0)
 */
export const checkStakingEligibility = (userData) => {
  if (!userData || userData.balance <= 0) {
    return {
      eligible: false,
      message: "Not Eligible For Staking Game. Balance must be greater than 0.",
    };
  }
  return { eligible: true };
};

/**
 * Place a stake by a player (atomically deduct from balance)
 */
export const placeStake = async (roomId, userData, stakeAmount, currentCycle) => {
  if (stakeAmount <= 0) {
    throw new Error("Stake amount must be greater than 0");
  }

  if (stakeAmount > userData.balance) {
    throw new Error("Insufficient balance for this stake");
  }

  try {
    // Use transaction for atomicity
    await runTransaction(db, async (transaction) => {
      // Check if player already staked
      const existingStakesQuery = query(
        collection(db, "rooms", roomId, "stakes"),
        where("playerPublicKey", "==", userData.publicKey),
        where("gameCycle", "==", currentCycle)
      );

      const existingStakes = await getDocs(existingStakesQuery);

      if (!existingStakes.empty) {
        throw new Error("You have already placed a stake this cycle");
      }

      // Deduct from user balance
      const userRef = doc(db, "users", userData.email);
      const userSnap = await transaction.get(userRef);

      if (!userSnap.exists()) {
        throw new Error("User not found");
      }

      const currentBalance = userSnap.data().balance;
      if (currentBalance < stakeAmount) {
        throw new Error("Insufficient balance");
      }

      transaction.update(userRef, {
        balance: increment(-stakeAmount),
      });

      // Record the stake
      const stakeRef = doc(collection(db, "rooms", roomId, "stakes"));
      transaction.set(stakeRef, {
        playerPublicKey: userData.publicKey,
        playerEmail: userData.email,
        stakeAmount: stakeAmount,
        stakedAt: serverTimestamp(),
        refunded: false,
        isWinner: false,
        gameCycle: currentCycle,
      });
    });
  } catch (error) {
    console.error("Error placing stake:", error);
    throw new Error(error.message || "Failed to place stake");
  }
};

/**
 * Update an existing stake (change the stake amount)
 * Refunds the difference back to the user balance
 */
export const updateStake = async (roomId, userData, newStakeAmount, currentCycle, existingStakeId) => {
  if (newStakeAmount <= 0) {
    throw new Error("Stake amount must be greater than 0");
  }

  try {
    // Use transaction for atomicity
    await runTransaction(db, async (transaction) => {
      // Get the existing stake
      const stakeRef = doc(db, "rooms", roomId, "stakes", existingStakeId);
      const stakeSnap = await transaction.get(stakeRef);

      if (!stakeSnap.exists()) {
        throw new Error("Stake not found");
      }

      const existingStake = stakeSnap.data();
      const stakeDifference = newStakeAmount - existingStake.stakeAmount;

      // Get user balance
      const userRef = doc(db, "users", userData.email);
      const userSnap = await transaction.get(userRef);

      if (!userSnap.exists()) {
        throw new Error("User not found");
      }

      const currentBalance = userSnap.data().balance;

      // Check if user has enough balance to cover the increase
      if (stakeDifference > 0 && currentBalance < stakeDifference) {
        throw new Error("Insufficient balance to increase stake");
      }

      // Update user balance (deduct more if increasing, refund if decreasing)
      transaction.update(userRef, {
        balance: increment(-stakeDifference),
      });

      // Update the stake
      transaction.update(stakeRef, {
        stakeAmount: newStakeAmount,
      });
    });
  } catch (error) {
    console.error("Error updating stake:", error);
    throw new Error(error.message || "Failed to update stake");
  }
};

/**
 * Determine the staking winner
 * If stakes exist: highest stake, ties broken by earliest timestamp
 * If no stakes: first player alphabetically from readyPlayers
 */
export const determineStakingWinner = async (
  roomId,
  currentCycle,
  readyPlayers
) => {
  try {
    // Query all stakes for this cycle (without composite index)
    const stakesQuery = query(
      collection(db, "rooms", roomId, "stakes"),
      where("gameCycle", "==", currentCycle)
    );

    const stakesSnapshot = await getDocs(stakesQuery);

    if (!stakesSnapshot.empty) {
      // Stakes exist - sort in memory and return highest staker
      const stakes = stakesSnapshot.docs.map((doc) => doc.data());

      // Sort by stake amount (descending) then by timestamp (ascending)
      stakes.sort((a, b) => {
        if (b.stakeAmount !== a.stakeAmount) {
          return b.stakeAmount - a.stakeAmount;
        }
        // Handle Firestore Timestamp objects
        const timeA = a.stakedAt?.toDate?.().getTime?.() || a.stakedAt?.getTime?.() || 0;
        const timeB = b.stakedAt?.toDate?.().getTime?.() || b.stakedAt?.getTime?.() || 0;
        return timeA - timeB;
      });

      const winningStake = stakes[0];
      return {
        publicKey: winningStake.playerPublicKey,
        email: winningStake.playerEmail,
        amount: winningStake.stakeAmount,
      };
    }

    // No stakes - select first ready player alphabetically
    if (!readyPlayers || readyPlayers.length === 0) {
      throw new Error("No ready players available");
    }

    const sortedPlayers = [...readyPlayers].sort((a, b) =>
      a.localeCompare(b)
    );
    const selectedPlayerPublicKey = sortedPlayers[0];

    // Get player details from users collection
    const usersQuery = query(
      collection(db, "users"),
      where("publicKey", "==", selectedPlayerPublicKey)
    );

    const usersSnapshot = await getDocs(usersQuery);

    if (!usersSnapshot.empty) {
      const userData = usersSnapshot.docs[0].data();
      return {
        publicKey: userData.publicKey,
        email: userData.email,
        amount: 0, // No stake placed
      };
    }

    throw new Error("Could not determine winner");
  } catch (error) {
    console.error("Error determining winner:", error);
    throw error;
  }
};

/**
 * Refund all losing stakes
 */
export const refundLosingStakes = async (
  roomId,
  winnerPublicKey,
  currentCycle
) => {
  try {
    // Get all stakes for this cycle (without composite index)
    const stakesQuery = query(
      collection(db, "rooms", roomId, "stakes"),
      where("gameCycle", "==", currentCycle)
    );

    const stakesSnapshot = await getDocs(stakesQuery);

    // Process refunds in batch
    const refundPromises = stakesSnapshot.docs.map(async (stakeDoc) => {
      const stake = stakeDoc.data();
      const isWinner = stake.playerPublicKey === winnerPublicKey;

      await runTransaction(db, async (transaction) => {
        // Mark stake with winner status
        transaction.update(stakeDoc.ref, { isWinner });

        // Refund losing stakes only
        if (!isWinner) {
          transaction.update(stakeDoc.ref, { refunded: true });

          // Refund to user balance
          const userRef = doc(db, "users", stake.playerEmail);
          transaction.update(userRef, {
            balance: increment(stake.stakeAmount),
          });
        }
      });
    });

    await Promise.all(refundPromises);
  } catch (error) {
    console.error("Error refunding stakes:", error);
    throw error;
  }
};

/**
 * Assign roles after staking game ends
 */
export const assignRolesAfterStaking = async (
  roomId,
  winnerPublicKey,
  allPlayerPublicKeys
) => {
  try {
    // Separate winner from losers
    const participantKeys = allPlayerPublicKeys.filter(
      (pk) => pk !== winnerPublicKey
    );

    // Update room with new roles
    const roomRef = doc(db, "rooms", roomId);
    await updateDoc(roomRef, {
      miners: [winnerPublicKey],
      participants: participantKeys,
      stakingWinner: winnerPublicKey,
      stakingWinnerTimestamp: new Date(),
    });
  } catch (error) {
    console.error("Error assigning roles:", error);
    throw error;
  }
};

/**
 * Get real-time stakes leaderboard for display
 * Returns all players with their stakes, sorted by amount then alphabetically
 */
export const getStakesLeaderboard = async (
  roomId,
  currentCycle,
  allPlayers
) => {
  try {
    // Get all stakes for this cycle
    const stakesQuery = query(
      collection(db, "rooms", roomId, "stakes"),
      where("gameCycle", "==", currentCycle)
    );

    const stakesSnapshot = await getDocs(stakesQuery);

    // Build stakes map
    const stakesMap = {};
    const stakesTimestampMap = {};

    stakesSnapshot.forEach((doc) => {
      const stake = doc.data();
      stakesMap[stake.playerPublicKey] = stake.stakeAmount;
      stakesTimestampMap[stake.playerPublicKey] = stake.stakedAt;
    });

    // Create leaderboard with all players
    const leaderboard = allPlayers.map((playerData) => ({
      publicKey: playerData.publicKey,
      email: playerData.email,
      stakeAmount: stakesMap[playerData.publicKey] || 0,
      stakedAt: stakesTimestampMap[playerData.publicKey] || null,
    }));

    // Sort by stake amount (descending), then by stake time (ascending), then alphabetically
    leaderboard.sort((a, b) => {
      if (b.stakeAmount !== a.stakeAmount) {
        return b.stakeAmount - a.stakeAmount;
      }

      // For ties, sort by time (earliest first)
      if (a.stakedAt && b.stakedAt) {
        return a.stakedAt.getTime() - b.stakedAt.getTime();
      }

      // If one has staked and other hasn't, staked comes first
      if (a.stakedAt && !b.stakedAt) return -1;
      if (!a.stakedAt && b.stakedAt) return 1;

      // If neither has staked, sort alphabetically
      return a.email.localeCompare(b.email);
    });

    return leaderboard;
  } catch (error) {
    console.error("Error getting stakes leaderboard:", error);
    throw error;
  }
};
